import { AddFriendWithSlug } from "@/components/Dialogs/AddFriend";
import { UserPopover } from "@/components/Popover/User";
import Icon from "@/icons";
import { useQueryClient } from "@tanstack/react-query";
import Tippy from "@tippyjs/react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Friend } from "types";

export default function FriendsList() {
    const queryClient = useQueryClient();
    const getUser = queryClient.getQueryState(["user"]) as { data: any };

    const pendingFriends = getUser.data.friends.filter((friend: any) => friend.status === "pending");
    const acceptedFriends = getUser.data.friends.filter((friend: any) => friend.status === "accepted");
    const navigate = useNavigate();
    const createDm = async (id: string) => {
        let dm: any = await fetch(`__SITE_BASE__/api/v1/me/chats`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ users: [{ id }] }),
        });
        dm = await dm.json();
        if (dm.success) navigate(`/chat/${dm.dm.id}`);
        else toast.error(dm.message);
    }

    const changeStatus = async (friend: Friend, status: string) => {
        await fetch(`__SITE_BASE__/api/me/friends/${friend.users.filter((user) => user.id !== getUser!.data?.user.id)[0].id}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status, id: friend.users.filter((user) => user.id !== getUser!.data?.user.id)[0].id }),
        });
    }

    const cancel = async (friend: Friend) => {
        await fetch(`__SITE_BASE__/api/me/friends/null`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: friend.users.filter((user) => user.id !== getUser!.data?.user.id)[0].id }),
        });
    }

    return (
        <>
            <div className="max-h-screen overflow-auto p-4 sm:px-32" style={{ scrollbarWidth: "thin", scrollbarColor: "rgb(40, 160, 42) rgb(27 27 29)" }}>
                <div className="w-full flex justify-between items-center mb-4">
                    <h1 className="text-gray-300 font-semibold text-2xl mb-6">
                        Friends
                    </h1>
                    <Tippy content="Add Friend" placement="top">
                        <AddFriendWithSlug>
                            <button className="bg-brand-green text-white px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow duration-300">
                                <Icon name="plus" size="24" />
                            </button>
                        </AddFriendWithSlug>
                    </Tippy>
                </div>
                <div className="flex w-full items-center mb-6">
                    {acceptedFriends.length > 0 ? (
                        <div className="flex flex-col w-full gap-4">
                            {acceptedFriends
                                .filter((friend: any) => friend.users.some((user: any) => user.id !== getUser.data.user.id))
                                .map((friend: any, index: number) => (
                                    <div key={index} className="flex items-center gap-4 justify-between bg-brand-dark rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow duration-300">
                                        <UserPopover user={{ ...friend.users.find((user: any) => user.id !== getUser.data.user.id), roles: ["leave"] }}>
                                            <div className="flex items-center gap-4">
                                                {friend.users.find((user: any) => user.id !== getUser.data.user.id)?.profilePicture ? (
                                                    <img src={friend.users.find((user: any) => user.id !== getUser.data.user.id)?.profilePicture} className="w-16 h-16 rounded-full" />
                                                ) : (
                                                    <div className="w-16 h-16 bg-brand-back rounded-full flex items-center justify-center">
                                                        <h1 className="text-gray-300 font-semibold text-2xl">{friend.users.find((user: any) => user.id !== getUser.data.user.id)?.username[0]}</h1>
                                                    </div>
                                                )}
                                                <div className="flex flex-col">
                                                    <h1 className="text-gray-300 font-semibold text-lg">{friend.users.find((user: any) => user.id !== getUser.data.user.id)?.username}</h1>
                                                    <div className="flex gap-2 items-center">
                                                        <h1 className="text-gray-400 text-sm">Friends</h1>
                                                        <div className="bg-brand-green text-white rounded-full p-1">
                                                            <Icon name="check" size="12" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </UserPopover>
                                        <div className="flex items-center" onClick={() => createDm(friend.users.find((user: any) => user.id !== getUser.data.user.id)?.id)}>
                                            <Tippy content="Message" placement="top">
                                                <button className="bg-brand-green text-white px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow duration-300">
                                                    <Icon name="message" size="24" />
                                                </button>
                                            </Tippy>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <div className="flex flex-col w-full h-64 items-center justify-center">
                            <h1 className="text-gray-300 font-semibold text-2xl">No friends found</h1>
                        </div>
                    )}
                </div>
                <h1 className="text-gray-300 font-semibold text-2xl mb-6">
                    Pending Friend Requests
                </h1>
                <div className="flex w-full items-center mb-6">
                    {pendingFriends.length > 0 ? (
                        <div className="flex flex-col w-full gap-4">
                            {pendingFriends
                                .filter((friend: any) => friend.users.some((user: any) => user.id !== getUser.data.user.id))
                                .map((friend: any, index: number) => (
                                    <div key={index} className="flex items-center gap-4 justify-between bg-brand-dark rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow duration-300">
                                        <UserPopover user={{ ...friend.users.find((user: any) => user.id !== getUser.data.user.id), roles: ["leave"] }}>
                                            <div className="flex items-center gap-4">
                                                {friend.users.find((user: any) => user.id !== getUser.data.user.id)?.profilePicture ? (
                                                    <img src={friend.users.find((user: any) => user.id !== getUser.data.user.id)?.profilePicture} className="w-16 h-16 rounded-full" />
                                                ) : (
                                                    <div className="w-16 h-16 bg-brand-back rounded-full flex items-center justify-center">
                                                        <h1 className="text-gray-300 font-semibold text-2xl">{friend.users.find((user: any) => user.id !== getUser.data.user.id)?.username[0]}</h1>
                                                    </div>
                                                )}
                                                <div className="flex flex-col">
                                                    <h1 className="text-gray-300 font-semibold text-lg">{friend.users.find((user: any) => user.id !== getUser.data.user.id)?.username}</h1>
                                                    {friend.createdBy == getUser.data.user.id ? (
                                                        <div className="flex gap-2 items-center">
                                                            <h1 className="text-gray-400 text-sm">You sent a friend request</h1>
                                                            <div className="bg-green-500 text-white rounded-full p-1">
                                                                <Icon name="exclamation" size="12" />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex gap-2 items-center">
                                                            <h1 className="text-gray-400 text-sm">
                                                                {friend.users.find((user: any) => user.id !== getUser.data.user.id)?.username} sent a friend request
                                                            </h1>
                                                            <div className="bg-orange-500 text-white rounded-full p-1">
                                                                <Icon name="exclamation" size="12" />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </UserPopover>
                                        <div className="flex items-center gap-2">
                                            {friend.createdBy == getUser.data.user.id ? (
                                                <div className="flex gap-2">
                                                    <Tippy content="Cancel Request" placement="top">
                                                        <button className="bg-red-500 text-white px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow duration-300" onClick={() => cancel(friend)}>
                                                            X
                                                        </button>
                                                    </Tippy>
                                                </div>
                                            ) : (
                                                <div className="flex gap-2">
                                                    <button className="bg-green-500 text-white px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow duration-300" onClick={() => changeStatus(friend, "accepted")}>
                                                        Accept
                                                    </button>
                                                    <button className="bg-red-500 text-white px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow duration-300" onClick={() => changeStatus(friend, "rejected")}>
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                            <Tippy content="Message" placement="top">
                                                <button className="bg-brand-green text-white px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow duration-300" onClick={() => createDm(friend.users.find((user: any) => user.id !== getUser.data.user.id)?.id)}>
                                                    <Icon name="message" size="24" />
                                                </button>
                                            </Tippy>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <div className="flex flex-col w-full h-64 items-center justify-center">
                            <h1 className="text-gray-300 font-semibold text-2xl">No pending friend requests</h1>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
